<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import RuleCard from './components/RuleCard.vue';
import RuleEditorModal from './components/RuleEditorModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const selectedCategory = ref('all');
const showEditorModal = ref(false);
const showDeleteModal = ref(false);
const editingRule = ref(null);
const deletingRule = ref(null);
const creatingDefaults = ref(false);
const deleteDialogRef = ref(null);

const rules = computed(() => store.getters['leadScore/getRules'] || []);
const uiFlags = computed(() => store.getters['leadScore/getUIFlags'] || {});

const eventCategories = computed(() => [
  { id: 'all', name: t('LEAD_SCORE.CATEGORIES.ALL') },
  { id: 'messages', name: t('LEAD_SCORE.CATEGORIES.MESSAGES') },
  { id: 'pipeline', name: t('LEAD_SCORE.CATEGORIES.PIPELINE') },
  { id: 'labels', name: t('LEAD_SCORE.CATEGORIES.LABELS') },
  { id: 'conversation', name: t('LEAD_SCORE.CATEGORIES.CONVERSATION') },
  { id: 'contact', name: t('LEAD_SCORE.CATEGORIES.CONTACT') },
  { id: 'agents', name: t('LEAD_SCORE.CATEGORIES.AGENTS') },
]);

const getEventCategory = eventType => {
  const categoryMap = {
    message_received: 'messages',
    message_sent: 'messages',
    first_response: 'messages',
    stage_changed: 'pipeline',
    card_created: 'pipeline',
    card_assigned: 'pipeline',
    label_added: 'labels',
    label_removed: 'labels',
    conversation_opened: 'conversation',
    conversation_resolved: 'conversation',
    conversation_reopened: 'conversation',
    contact_profile_updated: 'contact',
    contact_email_added: 'contact',
    contact_phone_added: 'contact',
    agent_assigned: 'agents',
    agent_replied: 'agents',
  };
  return categoryMap[eventType] || 'custom';
};

const filteredRules = computed(() => {
  if (selectedCategory.value === 'all') return rules.value;
  return rules.value.filter(
    r => getEventCategory(r.event_type) === selectedCategory.value
  );
});

const getRuleCountByCategory = category => {
  if (category === 'all') return rules.value.length;
  return rules.value.filter(r => getEventCategory(r.event_type) === category)
    .length;
};

const openCreateModal = () => {
  editingRule.value = null;
  showEditorModal.value = true;
};

const openEditModal = rule => {
  editingRule.value = rule;
  showEditorModal.value = true;
};

const closeEditorModal = () => {
  showEditorModal.value = false;
  editingRule.value = null;
};

const saveRule = async ruleData => {
  try {
    if (editingRule.value) {
      await store.dispatch('leadScore/updateRule', {
        id: editingRule.value.id,
        ...ruleData,
      });
      showAlert(t('LEAD_SCORE.SETTINGS.RULE_UPDATED'));
    } else {
      await store.dispatch('leadScore/createRule', ruleData);
      showAlert(t('LEAD_SCORE.SETTINGS.RULE_CREATED'));
    }
    closeEditorModal();
  } catch (error) {
    showAlert(error.message || t('LEAD_SCORE.SETTINGS.ERROR'));
  }
};

const confirmDelete = rule => {
  deletingRule.value = rule;
  showDeleteModal.value = true;
  deleteDialogRef.value?.open();
};

const deleteRule = async () => {
  try {
    await store.dispatch('leadScore/deleteRule', deletingRule.value.id);
    showAlert(t('LEAD_SCORE.SETTINGS.RULE_DELETED'));
  } catch (error) {
    showAlert(error.message || t('LEAD_SCORE.SETTINGS.ERROR'));
  } finally {
    deleteDialogRef.value?.close();
    showDeleteModal.value = false;
    deletingRule.value = null;
  }
};

const toggleRule = async rule => {
  try {
    await store.dispatch('leadScore/updateRule', {
      id: rule.id,
      enabled: !rule.enabled,
    });
    showAlert(
      rule.enabled
        ? t('LEAD_SCORE.SETTINGS.RULE_DISABLED')
        : t('LEAD_SCORE.SETTINGS.RULE_ENABLED')
    );
  } catch (error) {
    showAlert(error.message || t('LEAD_SCORE.SETTINGS.ERROR'));
  }
};

const createDefaultRules = async () => {
  creatingDefaults.value = true;
  try {
    await store.dispatch('leadScore/createDefaultRules');
    showAlert(t('LEAD_SCORE.SETTINGS.DEFAULTS_CREATED'));
  } catch (error) {
    showAlert(error.message || t('LEAD_SCORE.SETTINGS.ERROR'));
  } finally {
    creatingDefaults.value = false;
  }
};

onMounted(() => {
  store.dispatch('leadScore/fetchRules');
});
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ $t('LEAD_SCORE.SETTINGS.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ $t('LEAD_SCORE.SETTINGS.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex gap-2">
        <Button
          variant="outline"
          :loading="creatingDefaults"
          @click="createDefaultRules"
        >
          {{ $t('LEAD_SCORE.SETTINGS.CREATE_DEFAULTS') }}
        </Button>
        <Button solid blue @click="openCreateModal">
          {{ $t('LEAD_SCORE.SETTINGS.ADD_RULE') }}
        </Button>
      </div>
    </div>

    <!-- Event Categories Tabs -->
    <div class="flex gap-2 border-b border-n-weak pb-4">
      <button
        v-for="category in eventCategories"
        :key="category.id"
        class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
        :class="[
          selectedCategory === category.id
            ? 'bg-n-brand text-white'
            : 'text-n-slate-11 hover:bg-n-alpha-2',
        ]"
        @click="selectedCategory = category.id"
      >
        {{ category.name }}
        <span
          v-if="getRuleCountByCategory(category.id)"
          class="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-n-alpha-2"
        >
          {{ getRuleCountByCategory(category.id) }}
        </span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="uiFlags.isFetching" class="flex justify-center py-12">
      <Spinner size="large" />
    </div>

    <!-- Rules List -->
    <div v-else-if="filteredRules.length" class="space-y-3">
      <RuleCard
        v-for="rule in filteredRules"
        :key="rule.id"
        :rule="rule"
        @edit="openEditModal"
        @delete="confirmDelete"
        @toggle="toggleRule"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <div
        class="size-16 rounded-full bg-n-alpha-2 flex items-center justify-center mb-4"
      >
        <span class="i-lucide-trending-up size-8 text-n-slate-11" />
      </div>
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        {{ $t('LEAD_SCORE.SETTINGS.EMPTY.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4 max-w-md">
        {{ $t('LEAD_SCORE.SETTINGS.EMPTY.DESCRIPTION') }}
      </p>
      <Button solid blue @click="createDefaultRules">
        {{ $t('LEAD_SCORE.SETTINGS.CREATE_DEFAULTS') }}
      </Button>
    </div>

    <!-- Rule Editor Modal -->
    <RuleEditorModal
      v-if="showEditorModal"
      :rule="editingRule"
      @close="closeEditorModal"
      @save="saveRule"
    />

    <!-- Delete Confirmation Dialog -->
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('LEAD_SCORE.SETTINGS.DELETE.TITLE')"
      :description="$t('LEAD_SCORE.SETTINGS.DELETE.MESSAGE')"
      :confirm-button-label="$t('LEAD_SCORE.SETTINGS.DELETE.CONFIRM')"
      @confirm="deleteRule"
      @close="showDeleteModal = false"
    />
  </div>
</template>
